import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const CoronaryArteryDiseaseFH = () => {
  const manifestations = [
    {
      number: '1',
      title: 'Premature Coronary Artery Disease',
      content: '"Premature" means occurring earlier than expected: Men <55 years and Women <65 years. In FH, heart attacks can occur in the 30s or 40s, and in severe cases even during teenage years. This early onset is one of the strongest clues pointing toward undiagnosed FH.',
      icon: 'ri-time-line',
      color: '#f04e58'
    },
    {
      number: '2',
      title: 'Stable Angina',
      content: 'Stable angina is chest pain that occurs predictably with exertion and improves with rest. It signals narrowed coronary arteries that cannot deliver enough oxygen during physical activity. In FH patients, stable angina may appear decades earlier than normal.',
      icon: 'ri-heart-pulse-line',
      color: '#0d9488'
    },
    {
      number: '3',
      title: 'Unstable Angina',
      content: 'Unstable angina is more dangerous: occurs unpredictably, may happen at rest, and worsens over time. It often indicates a rupturing plaque — a warning sign that a heart attack may be imminent.',
      icon: 'ri-alert-line',
      color: '#e67e22'
    },
    {
      number: '4',
      title: 'Acute Coronary Syndrome (ACS)',
      content: 'Acute Coronary Syndrome (ACS) is an umbrella term that includes unstable angina, non-ST elevation heart attack, and ST-elevation heart attack. ACS represents sudden blockage of coronary blood flow. FH patients are at significantly higher lifetime risk of ACS due to heavy LDL burden.',
      icon: 'ri-emergency-line',
      color: '#8b5cf6'
    },
    {
      number: '5',
      title: 'Myocardial Infarction (Heart Attack)',
      content: 'A myocardial infarction occurs when blood flow is completely blocked, causing heart muscle damage. In untreated FH: lifetime heart attack risk can exceed 50%, events often occur before age 50, and recurrence is common without aggressive therapy. Early LDL control dramatically reduces this risk.',
      icon: 'ri-heart-add-line',
      color: '#f04e58'
    },
    {
      number: '6',
      title: 'Silent Ischemia',
      content: 'Not all heart damage causes pain. Silent ischemia refers to reduced blood flow without noticeable symptoms, often detected only on stress tests or imaging. This is especially concerning in FH because arterial damage may already be advanced before symptoms appear.',
      icon: 'ri-eye-off-line',
      color: '#14b8a6'
    },
    {
      number: '7',
      title: 'Multi-Vessel Coronary Disease',
      content: 'FH patients often develop plaque in multiple coronary arteries simultaneously. Multi-vessel disease increases surgical complexity, raises risk of heart failure, and often requires bypass surgery instead of stenting. This reflects the systemic nature of lifelong LDL exposure.',
      icon: 'ri-route-line',
      color: '#f59e0b'
    },
    {
      number: '8',
      title: 'Left Main Coronary Artery Disease',
      content: 'The left main artery supplies a large portion of the heart. Blockage here is particularly dangerous: high risk of fatal heart attack and often requires urgent bypass surgery. Severe FH — especially in Homozygous Familial Hypercholesterolemia — significantly increases risk of early left main disease.',
      icon: 'ri-arrow-left-right-line',
      color: '#ec4899'
    },
    {
      number: '9',
      title: 'Recurrent Cardiac Events',
      content: 'Without aggressive LDL lowering, FH patients face repeat heart attacks, repeat stenting, and repeat hospitalizations. Even after one event, elevated LDL continues to fuel plaque progression unless treated intensively.',
      icon: 'ri-repeat-line',
      color: '#0d9488'
    },
    {
      number: '10',
      title: 'Sudden Cardiac Death',
      content: 'In some cases, the first symptom of FH-related CAD is sudden cardiac death due to fatal arrhythmias, massive heart attack, or complete coronary occlusion. This tragic outcome is often preventable with early screening and treatment.',
      icon: 'ri-skull-line',
      color: '#f04e58'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Understanding CAD in FH Patients
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Coronary Artery Disease (CAD) + <br />
              Familial Hypercholesterolemia (FH)
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Understanding the accelerated cardiovascular risk in patients with inherited high cholesterol
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Coronary Artery Disease (CAD): The Silent Threat Behind Familial Hypercholesterolemia
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                Coronary Artery Disease (CAD) remains the leading cause of death worldwide. Yet for individuals living with Familial Hypercholesterolemia (FH), the risk is not just elevated — it is accelerated.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                FH is a genetic condition that causes lifelong elevation of LDL ("bad") cholesterol. Over decades, this excess LDL quietly damages coronary arteries, often leading to heart disease 20–30 years earlier than in the general population.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Understanding how CAD develops — and how it presents — is critical to recognizing the life-saving importance of early FH diagnosis.
              </p>
            </div>
          </div>

          {/* What is CAD */}
          <div className="bg-[#fef9f9] rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              What is Coronary Artery Disease?
            </h3>
            <p className="text-gray-700 leading-relaxed">
              CAD occurs when cholesterol-rich plaque builds up inside the coronary arteries, narrowing blood flow to the heart muscle. In FH, this process begins early — sometimes even in childhood.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Over time, arteries stiffen, narrow, and become unstable. The result can range from chest discomfort to sudden cardiac death.
            </p>
          </div>

          {/* Image */}
          <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/9188f86badb64dea2117ff70c189a7cf.jpg"
              alt="Coronary Artery Disease with plaque buildup"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Manifestations Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-white to-[#faf5f8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How CAD Manifests in Familial Hypercholesterolemia
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Ten critical presentations of coronary artery disease in FH patients
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {manifestations.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-sm"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.number}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">Critical Statistics</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">&gt;50%</div>
                <p className="text-white/90 text-sm">Lifetime heart attack risk in untreated FH</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">20-30</div>
                <p className="text-white/90 text-sm">Years earlier CAD develops in FH patients</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">&lt;50</div>
                <p className="text-white/90 text-sm">Age when heart attacks often occur in FH</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New Content Section - Why FH Changes the Story */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6">
          {/* Why FH Changes the Story */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Familial Hypercholesterolemia Changes the Story
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                In most people, CAD develops gradually over decades due to lifestyle and aging.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>In FH:</strong>
              </p>
              <ul className="list-none space-y-2 mb-6 ml-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1"></i>
                  <span className="text-gray-700">Elevated LDL is present from birth</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1"></i>
                  <span className="text-gray-700">Arteries accumulate damage year after year</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] mt-1"></i>
                  <span className="text-gray-700">Risk compounds with time</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                This concept is called <strong>"cholesterol year burden"</strong> — the total lifetime exposure to LDL.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The longer LDL remains high, the greater the arterial injury.
              </p>
            </div>
          </div>

          {/* The Good News */}
          <div className="bg-gradient-to-r from-[#0d9488]/10 to-[#14b8a6]/10 rounded-2xl p-8 mb-16 border-l-4 border-[#0d9488]">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <i className="ri-heart-pulse-fill text-[#0d9488]"></i>
              The Good News: CAD in FH Is Preventable
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When diagnosed early and treated aggressively:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0d9488] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">LDL can be reduced by <strong>50–70% or more</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0d9488] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">Heart attack risk drops by up to <strong>80%</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0d9488] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">Life expectancy approaches normal</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Modern therapies — statins, ezetimibe, PCSK9 inhibitors, and in severe cases LDL apheresis — can dramatically alter outcomes.
            </p>
          </div>

          {/* The Awareness Gap */}
          <div className="bg-white rounded-2xl p-8 mb-16 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <i className="ri-error-warning-fill text-[#f04e58]"></i>
              The Awareness Gap
            </h3>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="text-center p-6 bg-[#fef9f9] rounded-xl">
                <div className="text-4xl font-bold text-[#f04e58] mb-2">1 in 250</div>
                <p className="text-gray-600">people has FH</p>
              </div>
              <div className="text-center p-6 bg-[#fef9f9] rounded-xl">
                <div className="text-4xl font-bold text-[#f04e58] mb-2">90%</div>
                <p className="text-gray-600">remain undiagnosed</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Many families discover FH only after a premature heart attack.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Recognizing CAD patterns — especially:
            </p>
            <ul className="list-none space-y-2 mt-4 ml-4">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1"></i>
                <span className="text-gray-700">Early heart attacks</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1"></i>
                <span className="text-gray-700">Multi-vessel disease at young age</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] mt-1"></i>
                <span className="text-gray-700">Family history of early cardiac death</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              — should immediately trigger evaluation for FH.
            </p>
          </div>

          {/* What You Can Do */}
          <div className="bg-gradient-to-br from-[#f04e58]/10 to-[#ec4899]/10 rounded-2xl p-8 mb-16 border-l-4 border-[#f04e58]">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <i className="ri-hand-heart-fill text-[#f04e58]"></i>
              What You Can Do
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you or a family member has:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#f04e58] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">LDL &gt;190 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#f04e58] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">Heart attack before age 55</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#f04e58] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">Recurrent coronary disease</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#f04e58] text-white flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-gray-700">Strong family history of early heart disease</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Ask your physician about Familial Hypercholesterolemia testing.</strong>
            </p>
            <p className="text-gray-700 leading-relaxed">
              Early diagnosis does not just save one life — it allows cascade screening of the entire family.
            </p>
          </div>

          {/* Final Takeaway */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <i className="ri-lightbulb-flash-fill text-[#14b8a6]"></i>
              Final Takeaway
            </h3>
            <div className="space-y-4">
              <p className="text-white/90 leading-relaxed text-lg">
                Coronary Artery Disease is devastating.
              </p>
              <p className="text-white/90 leading-relaxed text-lg">
                But when CAD is driven by Familial Hypercholesterolemia, it is also <strong className="text-white">predictable and preventable</strong>.
              </p>
              <div className="grid md:grid-cols-3 gap-4 my-6">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-[#14b8a6] mb-2">1</div>
                  <p className="text-white/90 text-sm">Awareness is the first step</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-[#14b8a6] mb-2">2</div>
                  <p className="text-white/90 text-sm">Testing is the second</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-[#14b8a6] mb-2">3</div>
                  <p className="text-white/90 text-sm">Early treatment is the life-saving difference</p>
                </div>
              </div>
              <p className="text-white leading-relaxed text-lg font-semibold">
                If we identify FH early, we can stop premature heart disease before it begins.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Early Detection Saves Lives
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            If you have a family history of early heart disease or high cholesterol, don't wait for symptoms. Early screening and aggressive LDL control can prevent these devastating outcomes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/consultation"
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white rounded-full font-semibold hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-[#f04e58]/20"
            >
              Book a Free Consultation
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/topics"
              className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:border-[#f04e58] hover:text-[#f04e58] transition-all cursor-pointer whitespace-nowrap"
            >
              Learn More About FH
              <i className="ri-book-open-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoronaryArteryDiseaseFH;
